// Settings → Ledger Mapping (V-12): the Application API's routes for the business's map from its accounts, suppliers
// and customers and Dimension values to Tally's names (ADR-0035).
import { call, type Fetch } from "../../http.js";

export type MappingKind = "account" | "counterparty" | "dimension_value";

/** One LAMP thing on a list, with its name in Tally when it is mapped. */
export interface MappingRow {
  readonly kind: MappingKind;
  readonly subject: string;
  readonly lamp: string;
  /** Its statement group, the kind of party, or its Dimension's label. */
  readonly detail: string;
  readonly name: string | null;
}

export interface LedgerMappingLists {
  readonly may: { readonly map: boolean };
  readonly accounts: readonly MappingRow[];
  readonly parties: readonly MappingRow[];
  readonly values: readonly MappingRow[];
}

export interface Mapping {
  readonly kind: MappingKind;
  readonly subject: string;
  readonly name: string;
}

export interface Proposal {
  readonly proposed: readonly (Mapping & { readonly lamp: string; readonly label?: string; readonly by: string })[];
  readonly unmatched: readonly {
    readonly kind: MappingKind;
    readonly subject: string;
    readonly lamp: string;
    readonly label?: string;
    readonly reason: string;
    readonly candidates?: readonly string[];
  }[];
  readonly new_expense_accounts: readonly { readonly name: string; readonly group: string }[];
  readonly new_dimension_values?: {
    readonly dimension: string;
    readonly label: string;
    readonly names: readonly string[];
  };
}

export interface Confirmed {
  readonly changes: readonly (Mapping & { readonly previousName?: string })[];
  readonly added_accounts: readonly string[];
  readonly added_values: readonly string[];
}

/** A name from the last masters export: a ledger with its group, or a cost centre. */
export interface BooksName {
  readonly kind: "ledger" | "cost_centre";
  readonly name: string;
  readonly group?: string;
  /** A party ledger, by the groups the Application API reads as party groups: only a supplier or customer maps to one. */
  readonly party: boolean;
}

export interface LedgerMappingApi {
  list(): Promise<LedgerMappingLists>;
  names(search: string): Promise<{ readonly masters: readonly BooksName[] }>;
  map(mapping: Mapping): Promise<Mapping & { readonly previousName?: string }>;
  propose(xml: string): Promise<Proposal>;
  confirm(input: {
    xml: string;
    mappings: readonly Mapping[];
    add_expense_accounts: readonly string[];
    add_dimension_values: readonly string[];
  }): Promise<Confirmed>;
}

export function createLedgerMappingApi(fetcher: Fetch): LedgerMappingApi {
  return {
    list: () => call<LedgerMappingLists>(fetcher, "/api/settings/ledger-mapping"),
    names: (search) =>
      call<{ masters: BooksName[] }>(
        fetcher,
        `/api/settings/ledger-mapping/names?search=${encodeURIComponent(search)}`,
      ),
    map: (mapping) => call<Mapping>(fetcher, "/api/settings/ledger-mapping", { json: mapping }),
    propose: (xml) => call<Proposal>(fetcher, "/api/settings/ledger-mapping/proposal", { json: { xml } }),
    confirm: (input) => call<Confirmed>(fetcher, "/api/settings/ledger-mapping/confirm", { json: input }),
  };
}
